import React, {ReactElement} from "react"
import {kebabCase, startCase} from "lodash"
import numeral from "numeral"
import moment from "moment"
import classNames from "classnames"
import {Authorization, getDeclineReason, getDeclinedByDisplay} from "../../resources/authorization"
import {DataTableCell} from "../DataTable/DataTable"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"

export enum AccountAuthorizationsColumns {
	id = "Id",
	last4Digits = "Card Last 4 Digits",
	merchantName = "Merchant Name",
	status = "Status",
	reason = "Reason",
	declinedBy = "Declined By",
	createdAt = "Created At",
	amount = "Amount",
	actions = "Actions",
}

export type AllowedAccountAuthorizationsColumns =
	| AccountAuthorizationsColumns.id
	| AccountAuthorizationsColumns.last4Digits
	| AccountAuthorizationsColumns.merchantName
	| AccountAuthorizationsColumns.status
	| AccountAuthorizationsColumns.reason
	| AccountAuthorizationsColumns.declinedBy
	| AccountAuthorizationsColumns.createdAt
	| AccountAuthorizationsColumns.amount
	| AccountAuthorizationsColumns.actions

/** Builds table cells in column order; used by the row and by tests to assert column alignment. */
export function buildAuthorizationRowCells(
	au: Authorization,
	includedColumns: Array<AccountAuthorizationsColumns>,
	options?: {onReleaseAuthorization?: () => void}
): Array<ReactElement> {
	const contentColumns: Partial<Record<AllowedAccountAuthorizationsColumns, ReactElement>> = {}

	if (includedColumns.includes(AccountAuthorizationsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell>
				<span className="data-table-id"> {au.id} </span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.last4Digits)) {
		contentColumns["Card Last 4 Digits"] = <DataTableCell>{au.attributes.cardLast4Digits}</DataTableCell>
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.merchantName)) {
		contentColumns["Merchant Name"] = <DataTableCell>{au.attributes.merchant.name}</DataTableCell>
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.status)) {
		contentColumns["Status"] = (
			<DataTableCell>
				<div className={classNames("authorization-status", kebabCase(au.attributes.status))}>
					{startCase(au.attributes.status)}
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.reason)) {
		contentColumns["Reason"] = (
			<DataTableCell>
				<div className="declined-reason">{getDeclineReason(au.attributes.declineReason, au.attributes.status)}</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.declinedBy)) {
		contentColumns["Declined By"] = (
			<DataTableCell>{getDeclinedByDisplay(au.attributes.status, au.attributes.declinedBy)}</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(au.attributes.createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<b>{numeral(au.attributes.amount / 100).format("$0,0.00")}</b>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(AccountAuthorizationsColumns.actions)) {
		contentColumns["Actions"] = (
			<DataTableActionButton enableActions={true}>
				<DataTableActionItem
					title={"Release an authorization"}
					icon={"streamline-icon-navigation-arrows-right-2@20x20 (1)"}
					onClick={() => options?.onReleaseAuthorization?.()}
				/>
			</DataTableActionButton>
		)
	}

	const content: Array<ReactElement> = []

	includedColumns.forEach((col, i) => {
		if (!Object.prototype.hasOwnProperty.call(contentColumns, col)) {
			return
		}
		const cell = contentColumns[col]
		const column = {...(cell ?? <DataTableCell />)} as ReactElement
		column.key = i.toString()
		content.push(column)
	})

	return content
}
